'use client';

import {
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseInfiniteQueryOptions,
  type QueryKey,
  type InfiniteData,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

/**
 * Middleware function type for query operations
 * Can be used to transform data, add logging, handle errors, etc.
 */
export type QueryMiddleware<TData = unknown, TError = unknown> = {
  /**
   * Called before the query function executes
   */
  onBefore?: (queryKey: QueryKey) => void | Promise<void>;

  /**
   * Called after successful query execution
   */
  onSuccess?: (data: TData, queryKey: QueryKey) => void | Promise<void>;

  /**
   * Called when query fails
   */
  onError?: (error: TError, queryKey: QueryKey) => void | Promise<void>;

  /**
   * Called after query completes (success or error)
   */
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    queryKey: QueryKey
  ) => void | Promise<void>;
};

/**
 * Global middleware stack that applies to all queries
 */
const globalMiddleware: QueryMiddleware<any, any>[] = [];

/**
 * Register global middleware that applies to all queries
 */
export function registerGlobalQueryMiddleware(middleware: QueryMiddleware<any, any>) {
  globalMiddleware.push(middleware);
}

/**
 * Clear all global middleware
 */
export function clearGlobalQueryMiddleware() {
  globalMiddleware.length = 0;
}

/**
 * Custom useQuery hook with middleware support
 *
 * @example
 * ```ts
 * const { data } = useCustomQuery({
 *   queryKey: ['users', userId],
 *   queryFn: () => fetchUser(userId),
 *   middleware: {
 *     onSuccess: (data) => {
 *       console.log('User fetched:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Failed to fetch user:', error);
 *     }
 *   }
 * });
 * ```
 */
export function useCustomQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
    middleware?: QueryMiddleware<TData, TError>;
  }
) {
  const { middleware, queryFn, ...queryOptions } = options;

  // Combine global and local middleware
  const allMiddleware = [
    ...globalMiddleware,
    ...(middleware ? [middleware] : []),
  ] as QueryMiddleware<TData, TError>[];

  // Track previous state to detect changes
  const prevDataRef = useRef<TData | undefined>(undefined);
  const prevErrorRef = useRef<TError | null>(null);
  const beforeCalledRef = useRef(false);

  // Wrap the query function with middleware
  const wrappedQueryFn =
    typeof queryFn === 'function'
      ? async (context: any) => {
          // Execute onBefore middleware only once per query execution
          if (!beforeCalledRef.current) {
            for (const mw of allMiddleware) {
              if (mw.onBefore) {
                await mw.onBefore(queryOptions.queryKey);
              }
            }
            beforeCalledRef.current = true;
          }

          try {
            const result = await queryFn(context);
            return result;
          } catch (error) {
            // Execute onError middleware
            for (const mw of allMiddleware) {
              if (mw.onError) {
                await mw.onError(error as TError, queryOptions.queryKey);
              }
            }
            throw error;
          }
        }
      : queryFn;

  const result = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    ...queryOptions,
    queryFn: wrappedQueryFn as typeof queryFn,
  });

  // Use effect to handle success/settled callbacks
  useEffect(() => {
    const hasDataChanged = result.data !== prevDataRef.current;
    const hasErrorChanged = result.error !== prevErrorRef.current;

    if (hasDataChanged || hasErrorChanged) {
      // Execute onSuccess middleware when data changes and is successful
      if (result.isSuccess && result.data !== undefined && hasDataChanged) {
        (async () => {
          for (const mw of allMiddleware) {
            if (mw.onSuccess) {
              await mw.onSuccess(result.data as TData, queryOptions.queryKey);
            }
          }
        })();
      }

      // Execute onSettled middleware when state changes
      if (!result.isLoading && (hasDataChanged || hasErrorChanged)) {
        (async () => {
          for (const mw of allMiddleware) {
            if (mw.onSettled) {
              await mw.onSettled(result.data, result.error as TError | null, queryOptions.queryKey);
            }
          }
        })();
      }

      prevDataRef.current = result.data;
      prevErrorRef.current = result.error as TError | null;
    }
  }, [
    result.data,
    result.error,
    result.isSuccess,
    result.isLoading,
    queryOptions.queryKey,
    allMiddleware,
  ]);

  // Reset beforeCalled when query key changes
  useEffect(() => {
    beforeCalledRef.current = false;
  }, [JSON.stringify(queryOptions.queryKey)]);

  return result;
}

/**
 * Middleware function type for infinite query operations
 */
export type InfiniteQueryMiddleware<TData = unknown, TError = unknown> = {
  /**
   * Called before the query function executes
   */
  onBefore?: (queryKey: QueryKey, pageParam?: unknown) => void | Promise<void>;

  /**
   * Called after successful page fetch
   */
  onSuccess?: (data: InfiniteData<TData>, queryKey: QueryKey) => void | Promise<void>;

  /**
   * Called when query fails
   */
  onError?: (error: TError, queryKey: QueryKey) => void | Promise<void>;

  /**
   * Called after query completes (success or error)
   */
  onSettled?: (
    data: InfiniteData<TData> | undefined,
    error: TError | null,
    queryKey: QueryKey
  ) => void | Promise<void>;
};

/**
 * Global middleware stack for infinite queries
 */
const globalInfiniteMiddleware: InfiniteQueryMiddleware<any, any>[] = [];

/**
 * Register global middleware for infinite queries
 */
export function registerGlobalInfiniteQueryMiddleware(
  middleware: InfiniteQueryMiddleware<any, any>
) {
  globalInfiniteMiddleware.push(middleware);
}

/**
 * Clear all global infinite query middleware
 */
export function clearGlobalInfiniteQueryMiddleware() {
  globalInfiniteMiddleware.length = 0;
}

/**
 * Custom useInfiniteQuery hook with middleware support
 *
 * @example
 * ```ts
 * const { data, fetchNextPage } = useCustomInfiniteQuery({
 *   queryKey: ['posts'],
 *   queryFn: ({ pageParam }) => fetchPosts(pageParam),
 *   initialPageParam: 0,
 *   getNextPageParam: (lastPage) => lastPage.nextCursor,
 *   middleware: {
 *     onSuccess: (data) => {
 *       console.log('Pages fetched:', data.pages.length);
 *     }
 *   }
 * });
 * ```
 */
export function useCustomInfiniteQuery<
  TQueryFnData,
  TError = Error,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam> & {
    middleware?: InfiniteQueryMiddleware<TQueryFnData, TError>;
  }
) {
  const { middleware, queryFn, ...queryOptions } = options;

  // Combine global and local middleware
  const allMiddleware = [
    ...globalInfiniteMiddleware,
    ...(middleware ? [middleware] : []),
  ] as InfiniteQueryMiddleware<TQueryFnData, TError>[];

  // Track previous state to detect changes
  const prevDataRef = useRef<TData | undefined>(undefined);
  const prevErrorRef = useRef<TError | null>(null);

  // Wrap the query function with middleware
  const wrappedQueryFn =
    typeof queryFn === 'function'
      ? async (context: any) => {
          // Execute onBefore middleware
          for (const mw of allMiddleware) {
            if (mw.onBefore) {
              await mw.onBefore(queryOptions.queryKey, context.pageParam);
            }
          }

          try {
            const result = await queryFn(context);
            return result;
          } catch (error) {
            // Execute onError middleware
            for (const mw of allMiddleware) {
              if (mw.onError) {
                await mw.onError(error as TError, queryOptions.queryKey);
              }
            }
            throw error;
          }
        }
      : queryFn;

  const result = useInfiniteQuery<TQueryFnData, TError, TData, TQueryKey, TPageParam>({
    ...queryOptions,
    queryFn: wrappedQueryFn as typeof queryFn,
  });

  // Use effect to handle success/settled callbacks
  useEffect(() => {
    const hasDataChanged = result.data !== prevDataRef.current;
    const hasErrorChanged = result.error !== prevErrorRef.current;

    if (hasDataChanged || hasErrorChanged) {
      // Execute onSuccess middleware when data changes and is successful
      if (result.isSuccess && result.data !== undefined && hasDataChanged) {
        (async () => {
          for (const mw of allMiddleware) {
            if (mw.onSuccess) {
              await mw.onSuccess(result.data as InfiniteData<TQueryFnData>, queryOptions.queryKey);
            }
          }
        })();
      }

      // Execute onSettled middleware when state changes
      if (!result.isLoading && (hasDataChanged || hasErrorChanged)) {
        (async () => {
          for (const mw of allMiddleware) {
            if (mw.onSettled) {
              await mw.onSettled(
                result.data as InfiniteData<TQueryFnData> | undefined,
                result.error as TError | null,
                queryOptions.queryKey
              );
            }
          }
        })();
      }

      prevDataRef.current = result.data;
      prevErrorRef.current = result.error as TError | null;
    }
  }, [
    result.data,
    result.error,
    result.isSuccess,
    result.isLoading,
    queryOptions.queryKey,
    allMiddleware,
  ]);

  return result;
}
